import { storeGeocodeCache as storeGeocodeCacheOp } from '@gql/operations'
import { getLogger, type GqlExecutor } from '@hopdrive/sdk-core'
import { normalizeInputForAlias } from '../../utils/normalize'
import type { GeocodeResult } from '../../types'

export interface StoreGeocodeCacheParams {
  address: string
  geocodeResult: GeocodeResult
  executor?: GqlExecutor
}

/**
 * Store a geocode result in the global cache.
 * Fire-and-forget - errors are logged but not thrown.
 *
 * Cache entries expire after 30 days (Google Terms compliant).
 *
 * @see performance-optimizations.md#4-global-geocoding-cache
 */
export async function storeGeocodeCache(
  params: StoreGeocodeCacheParams
): Promise<void> {
  const log = getLogger()
  const normalizedAddress = normalizeInputForAlias(params.address)

  // 30 days from now
  const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)

  try {
    await storeGeocodeCacheOp(
      {
        normalizedAddress,
        geocodeResult: params.geocodeResult,
        expiresAt: expiresAt.toISOString(),
      },
      { executor: params.executor }
    )
  } catch (err) {
    const error = err as Error & { extensions?: { code?: string } }
    // Log but don't fail - cache is an optimization, not critical
    log.warn('lanes.geocoding.storeCacheFailed', {
      address: params.address,
      normalizedAddress,
      error: error.message,
    })
  }
}

/**
 * Fire-and-forget version that doesn't await.
 */
export function storeGeocodeCacheAsync(params: StoreGeocodeCacheParams): void {
  storeGeocodeCache(params).catch(() => {})
}
